import {
  InviteSource,
  CommonListParams,
  CommonListResponse,
  ArtWorkType,
  ArtistDetailType,
  UploadFileObjType,
  MediaUser,
  MediaUserDetail,
  MediaUserListSearchParams,
  NewMediaUserParams,
  SocialArtWorkType,
  SocialArtworkStatusEnum,
  SocialAssets,
  SocialOverviewData,
} from 'common/types';
import { TaskDetail } from 'common/types/manager';
import { FastApiAxiosInstance } from 'common/service/axiosInstances';

export const recommendedTools = {
  // 社媒用户列表 /api/v1/backend/mediaUser/list
  getMediaUserList: (params: CommonListParams & MediaUserListSearchParams) => {
    return FastApiAxiosInstance.post<CommonListResponse<MediaUser>>('/api/v1/backend/mediaUser/list', params);
  },

  // 获取未邀请的任务 /api/v1/backend/mediaUser/getUninvitedTasks
  getUninvitedTasks: (params: { mediaUserId: number }) => {
    return FastApiAxiosInstance.get<CommonListResponse<TaskDetail>>('api/v1/backend/mediaUser/getUninvitedTasks', {
      params,
    });
  },

  // 社媒用户统计 /api/v1/backend/mediaUser/numOverview
  getMediaUserOverview: () => {
    return FastApiAxiosInstance.get<SocialOverviewData>('api/v1/backend/mediaUser/numOverview');
  },

  // 社媒用户主页 /api/v1/backend/mediaUser/homePage
  getMediaUserInfo: (params: { mediaUserId: number }) => {
    return FastApiAxiosInstance.get<MediaUserDetail>('/api/v1/backend/mediaUser/homePage', {
      params,
    });
  },

  // 推荐工具社媒用户主页 /api/v1/backend/mediaUser/homePage
  getRecommendMediaUserInfo: (params: { mediaUserId: number }) => {
    return FastApiAxiosInstance.get<MediaUserDetail>('/api/v1/recommend/mediaUser/homePage', {
      params,
    });
  },

  // 复审社媒用户 /api/v1/backend/review/mediaUser
  reviewMediaUser: (params: { mediaUserId: number; isPass: boolean }) => {
    return FastApiAxiosInstance.post('/api/v1/backend/review/mediaUser', params);
  },

  getMediaUserWorks: (params: { mediaUserId: number; workStatus?: SocialArtworkStatusEnum[] } & CommonListParams) => {
    return FastApiAxiosInstance.post<CommonListResponse<ArtWorkType>>('/api/v1/backend/mediaUser/artWorks', params);
  },

  // 上传社媒作品 /api/v1/backend/mediaUser/uploadArtwork
  uploadMediaUserWorks: (params: { artworks: SocialArtWorkType[]; mediaUserId: number }) => {
    return FastApiAxiosInstance.post('/api/v1/backend/mediaUser/uploadArtwork', params);
  },

  // 删除作品 /api/v1/backend/mediaUser/deleteArtWork
  deleteMediaUserWorks: (params: { artworkIds: string[]; mediaUserId: number }) => {
    return FastApiAxiosInstance.post('/api/v1/backend/mediaUser/deleteArtWork', params);
  },

  //一键邀请
  inviteAll: (params: { mediaUserId: number; parentTaskIds: number[] }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/backend/mediaUser/inviteAll', params);
  },

  //一键暂不合作
  ignoreAll: (params: { mediaUserId: number; parentTaskIds: number[] }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/backend/ignoreRecommend', params);
  },

  // 媒体链接类型 /api/v1/backend/mediaUser/linkTypes
  getLinkTypes: () => {
    return FastApiAxiosInstance.get<{ label: string; value: number; example: string }[]>(
      '/api/v1/backend/mediaUser/linkTypes',
    );
  },

  // 校验社媒用户输入 /api/v1/backend/mediaUser/checkInput
  checkMediaUser: ({ nickname, link }: { nickname?: string; link?: string }) => {
    return FastApiAxiosInstance.post<number>('/api/v1/backend/mediaUser/checkInput', { nickname, link });
  },

  // 通过社媒链接获取作品，目前用于推荐创作者表单 /api/v1/backend/mediaUser/preloadWorks
  getPreloadWorks: (link: string) => {
    return FastApiAxiosInstance.post<CommonListResponse<SocialAssets>>('/api/v1/backend/mediaUser/preloadWorks', {
      link,
    });
  },

  getRecommendedMediaUser: (params: { parentTaskId: number }) => {
    return FastApiAxiosInstance.get<ArtistDetailType[]>('/api/v1/backend/mediaUser/recommended', { params });
  },

  // 【推荐社媒创作者】添加社媒用户 /api/v1/backend/mediaUser/add
  addMediaUser: (params: NewMediaUserParams) => {
    return FastApiAxiosInstance.post('/api/v1/backend/mediaUser/add', params);
  },

  // 【推荐社媒创作者】详情页更新社媒用户 /api/v1/backend/mediaUser/updateInfo
  updateMediaUser: (params: { mediaUserId: number } & Omit<NewMediaUserParams, 'artworks'>) => {
    return FastApiAxiosInstance.post('/api/v1/backend/mediaUser/updateInfo', params);
  },

  // 批量导入excel
  batchImportMediaUser: (list: UploadFileObjType[]) => {
    return FastApiAxiosInstance.post<null>('/api/v1/backend/mediaUser/excel/import', list);
  },
  // 绑定社媒用户
  bindMediaUser: (params: { mediaUserId: number; userId: number }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/backend/mediaUser/bindUser', params);
  },

  //批量移入或者移出美术作品
  batchImportFineArtwork: (params: { isWork: boolean; artworkId: string[]; mediaUserId: number }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/backend/mediaUser/workBatchVerify', params);
  },

  inviteMediaUser: (params: { mediaUserId: number; parentTaskId: number; inviteSource?: InviteSource }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/artist/inviteMediaUser', params);
  },
  recommendMediaUser: (params: {
    userCode: string;
    parentTaskId: number;
    recommendDesc: string;
    artWorks: ArtWorkType[];
    inviteSource?: InviteSource;
    recommendTaskId?: number;
    quotedPrice?: string;
    calendar?: string;
  }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/backend/mediaUser/recommend', params);
  },
  //更新推荐信息
  updateRecommendUser: (params: {
    biddenId: number;
    recommendDesc: string;
    artWorks: ArtWorkType[];
    quotedPrice?: string;
    calendar?: string;
  }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/backend/updateRecommendUser', params);
  },

  //取消推荐
  cancelRecommendUser: (params: { biddenId: number }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/backend/cancelRecommendUser', params);
  },

  copyRecommendUser: (params: { sourceId: number; targetId: number }) => {
    return FastApiAxiosInstance.post<null>('/api/v1/backend/copyRecommendUser', params);
  },

  getMediaUserWorksRecommend: (
    params: { mediaUserId: number; workStatus?: SocialArtworkStatusEnum[] } & CommonListParams,
  ) => {
    return FastApiAxiosInstance.post<CommonListResponse<ArtWorkType>>('/api/v1/recommend/getMediaArtworks', params);
  },
};
